import React from "react";

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTES = Array.from({ length: 60 }, (_, i) => i);   // 0..59

function to12Hour(value24) {
  if (!value24) return null;
  const [hStr, mStr] = value24.split(":");
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  const ampm = h >= 12 ? "PM" : "AM";
  let hour = h % 12;
  if (hour === 0) hour = 12;
  return { hour, minute: m, ampm };
}

function to24Hour(hour12, minute, ampm) {
  let h = hour12 % 12;
  if (ampm === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
}

// Market hours run 9:15 AM-3:30 PM, so for any exit time someone would
// realistically set, 9-11 is always morning and 12-8 is always afternoon.
// This is only a one-time convenience default applied when picking a new
// hour - the AM/PM buttons always let you override it directly afterward.
function defaultAmPm(hour12) {
  return hour12 >= 9 && hour12 <= 11 ? "AM" : "PM";
}

/**
 * Controlled 12-hour time picker. `value` is a 24-hour "HH:MM" string (or
 * ""/null for "not set") - same format the backend already stores for
 * entry_window_start/end, so this is a drop-in for any of those fields.
 * `onChange` receives the same 24-hour format back; callers never have to
 * think about AM/PM themselves.
 */
export default function ExitTimePicker({ value, onChange, disabled }) {
  const parsed = to12Hour(value);
  const hour = parsed ? parsed.hour : "";
  const minute = parsed ? parsed.minute : "";
  const ampm = parsed ? parsed.ampm : "PM";

  const handleHour = (e) => {
    const h = e.target.value;
    if (h === "") {
      onChange("");
      return;
    }
    const m = minute === "" ? 0 : minute;
    onChange(to24Hour(Number(h), m, parsed ? ampm : defaultAmPm(Number(h))));
  };

  const handleMinute = (e) => {
    if (hour === "") return;
    onChange(to24Hour(Number(hour), Number(e.target.value), ampm));
  };

  const handleAmPm = (nextAmPm) => {
    if (hour === "") return;
    onChange(to24Hour(Number(hour), minute === "" ? 0 : Number(minute), nextAmPm));
  };

  return (
    <div className="time-picker-12h">
      <select value={hour} onChange={handleHour} disabled={disabled}>
        <option value="">--</option>
        {HOURS.map((h) => (
          <option key={h} value={h}>{h}</option>
        ))}
      </select>
      <span className="muted">:</span>
      <select value={minute} onChange={handleMinute} disabled={disabled || hour === ""}>
        {MINUTES.map((m) => (
          <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
        ))}
      </select>
      <div className="ampm-toggle">
        <span
          className={`ampm-pill ${ampm === "AM" ? "active" : ""}`}
          onClick={() => handleAmPm("AM")}
        >
          AM
        </span>
        <span
          className={`ampm-pill ${ampm === "PM" ? "active" : ""}`}
          onClick={() => handleAmPm("PM")}
        >
          PM
        </span>
      </div>
      {value && (
        <span className="link-button" onClick={() => onChange("")}>
          Clear
        </span>
      )}
    </div>
  );
}
